/**
 * "Let Graphit set this up" (25i): pure helpers over the auto-setup plan
 * (`GET …/onboarding/ai/auto-setup/preview/`, and the plan the start POST
 * hands back). No fetches.
 */

export const PLAN_ACTIONS = Object.freeze(['read', 'create', 'skip'])

/**
 * The plan's rows, keeping only well-formed ones (external data).
 *
 * @param {{plan?: Array<Object>}|null|undefined} payload
 * @returns {Array<import('@/api/modules/rockOnboarding').AutoSetupPlanRow>}
 */
export function planRows(payload) {
  const rows = Array.isArray(payload?.plan) ? payload.plan : []
  return rows.filter((row) => row && typeof row.key === 'string' && row.key && PLAN_ACTIONS.includes(row.action))
}

/**
 * @param {{action: string}} row
 * @returns {boolean} the plan sets this row up (Graphit didn't skip it)
 */
export const isRunnable = (row) => row.action !== 'skip'

/**
 * A row's action once the person's own skips are applied.
 *
 * @param {{key: string, action: string}} row
 * @param {Set<string>} skipped
 * @returns {'read'|'create'|'skip'}
 */
export const effectiveAction = (row, skipped) => (skipped.has(row.key) ? 'skip' : row.action)

/**
 * Why Graphit skipped a row, as the plan's `reason_code` (backend 25i:
 * `needs_choice`, `needs_match`, `stale`, `empty`, `snapshot`, …). A plan
 * from before the codes falls back to matching the backend's sentences
 * (`auto_setup.py`), so the UI reads the same either way.
 */
const SKIP_KINDS = Object.freeze({
  needs_choice: 'choice',
  needs_match: 'match',
  stale: 'stale',
  empty: 'empty',
  snapshot: 'snapshot',
})

/** Fallback only: the sentences behind each kind, when a row has no `reason_code`. */
const REASON_TEXT_PATTERNS = [
  ['stale', /stopped getting new values/i],
  ['empty', /has no values yet/i],
  ['match', /^Match your (campuses|service times) first/i],
  ['choice', /^It needs\b|Choose it yourself/i],
]

/**
 * @param {{action: string, reason?: string, reason_code?: string}} row
 * @returns {'choice'|'match'|'stale'|'empty'|'snapshot'|null} what kind of skip it is (null: a plain skip, or not skipped)
 */
export function skipKind(row) {
  if (row.action !== 'skip') return null
  if (row.reason_code) return SKIP_KINDS[row.reason_code] || null
  const found = REASON_TEXT_PATTERNS.find(([, pattern]) => pattern.test(row.reason || ''))
  return found ? found[0] : null
}

/**
 * @param {Object} row
 * @returns {'stale'|'empty'|'snapshot'|null} a Rock metric Graphit won't read (a plain skip the person may still choose)
 */
export function staleKind(row) {
  const kind = skipKind(row)
  return ['stale', 'empty', 'snapshot'].includes(kind) ? kind : null
}

/**
 * @param {Object} row
 * @returns {boolean} Graphit skipped it only because campuses or service times need matching
 */
export const needsMatch = (row) => skipKind(row) === 'match'

/**
 * @param {Object} row
 * @returns {boolean} Graphit skipped it only because it needs the person's choice (a match included)
 */
export const needsChoice = (row) => ['choice', 'match'].includes(skipKind(row))

/**
 * @param {Array<{key: string, action: string, reason?: string}>} rows
 * @param {Set<string>} [skipped]
 * @returns {{read: number, create: number, needsChoice: number, skip: number}} `skip` excludes `needsChoice`
 */
export function planCounts(rows, skipped = new Set()) {
  const counts = { read: 0, create: 0, needsChoice: 0, skip: 0 }
  rows.forEach((row) => {
    if (needsChoice(row)) counts.needsChoice += 1
    else counts[effectiveAction(row, skipped)] += 1
  })
  return counts
}

/**
 * The keys a run sets up, in item order (the run's `items[].index` follows them).
 *
 * @param {Array<{key: string, action: string}>} rows
 * @param {Set<string>} [skipped]
 * @returns {Array<string>}
 */
export function runKeysOf(rows, skipped = new Set()) {
  return rows.filter((row) => effectiveAction(row, skipped) !== 'skip').map((row) => row.key)
}

/** Stand-in row keys for a run whose plan this page never saw (found on `/me`). */
const PLACEHOLDER_PREFIX = '#'

/**
 * @param {number} total
 * @returns {Array<string>}
 */
export function placeholderKeys(total) {
  const count = Number.isFinite(total) && total > 0 ? total : 0
  return Array.from({ length: count }, (_, index) => `${PLACEHOLDER_PREFIX}${index}`)
}
